import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Badge, Card, Col, Container, Form, ListGroup, Row, Spinner, Table } from 'react-bootstrap';
import axios from '../utils/api';
import ProductViewsDashboard from './ProductViewsDashboard';

const DASHBOARD_ITEMS = [
  {
    id: 'daily-registrations',
    title: 'Daily Registrations',
    description: 'New user registrations by date',
    endpoint: '/api/dashboard/daily-registrations',
    totalKey: 'total_registrations',
    totalLabel: 'Total Registrations',
    graphTitle: 'Daily New Registrations',
    graphDescription: 'Track user signups over selected dates.',
    emptyStateMessage: 'No registration records available for this range.',
  },
  {
    id: 'daily-orders',
    title: 'Daily Orders',
    description: 'Order placements by date',
    endpoint: '/api/dashboard/daily-orders',
    totalKey: 'total_orders',
    totalLabel: 'Total Orders',
    graphTitle: 'Daily Orders Count',
    graphDescription: 'Track placed orders over selected dates.',
    emptyStateMessage: 'No order records available for this range.',
    showPercentLines: true,
  },
  {
    id: 'top-selling',
    title: 'Top Selling',
    description: 'Top 20 products or variants by units sold',
    endpoint: '/api/dashboard/top-selling',
    totalKey: 'total_units_sold',
    totalLabel: 'Units Sold (Top 20)',
    graphTitle: 'Top 20 Selling Products / Variants',
    graphDescription: 'Analyze units sold and unit price for top performers.',
    emptyStateMessage: 'No sales data available for this range.',
    type: 'top-selling',
  },
  {
    id: 'user-login-rate',
    title: 'User Login Rate',
    description: 'Percent of users logging in by time bucket',
    endpoint: '/api/dashboard/login-rate',
    totalKey: 'total_users',
    totalLabel: 'Total Users',
    graphTitle: 'User Login Percentage',
    graphDescription: 'Shows what percentage of users logged in per selected timeline bucket.',
    emptyStateMessage: 'No login activity data available for this range.',
    type: 'login-rate',
    targetThresholdPercent: 30,
  },
  {
    id: 'critical-inventory',
    title: 'Critical Inventory',
    description: 'Variant SKU stock pressure by timeline',
    endpoint: '/api/dashboard/critical-inventory',
    totalKey: 'total_critical_rows',
    totalLabel: 'Critical SKU Rows',
    graphTitle: 'Critical Inventory Level',
    graphDescription: 'Flags SKU buckets where ((stock - ordered) / ordered) * 100 is below configured threshold.',
    emptyStateMessage: 'No critical inventory SKUs for selected range.',
    type: 'critical-inventory',
  },
  {
    id: 'product-views',
    title: 'Product Views',
    description: 'Products viewed by buyers by date, user, and product',
    type: 'product-views',
  },
];

const formatDate = (value) => {
  const year = value.getFullYear();
  const month = `${value.getMonth() + 1}`.padStart(2, '0');
  const day = `${value.getDate()}`.padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const getPresetRange = (preset) => {
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());

  if (preset === 'today') {
    return { startDate: formatDate(today), endDate: formatDate(today) };
  }

  if (preset === 'week') {
    const start = new Date(today);
    const dayOfWeek = start.getDay();
    start.setDate(start.getDate() - dayOfWeek);
    return { startDate: formatDate(start), endDate: formatDate(today) };
  }

  if (preset === 'month') {
    const start = new Date(today.getFullYear(), today.getMonth(), 1);
    return { startDate: formatDate(start), endDate: formatDate(today) };
  }

  if (preset === 'year') {
    const start = new Date(today.getFullYear(), 0, 1);
    return { startDate: formatDate(start), endDate: formatDate(today) };
  }

  return { startDate: formatDate(today), endDate: formatDate(today) };
};

const getCriticalSeverityBand = (criticalPercent) => {
  const value = Number(criticalPercent || 0);
  if (value < 0) {
    return { label: 'Severe (<0%)', rowClass: 'table-danger', badgeClass: 'severity-severe' };
  }
  if (value < 5) {
    return { label: 'High (0-5%)', rowClass: 'table-warning', badgeClass: 'severity-high' };
  }
  if (value < 10) {
    return { label: 'Moderate (5-10%)', rowClass: 'table-info', badgeClass: 'severity-moderate' };
  }
  return { label: 'Low (>=10%)', rowClass: '', badgeClass: 'severity-low' };
};

const aggregateTimelinePoints = (points, timelineView) => {
  const normalizedPoints = points.map((point) => {
    const count = Number(point.count || 0);
    const cancelledCount = Number(point.cancelled_count || 0);
    const returnedCount = Number(point.returned_count || 0);
    return {
      ...point,
      count,
      cancelled_count: cancelledCount,
      returned_count: returnedCount,
      cancelled_percent: count > 0 ? Number((((cancelledCount / count) * 100).toFixed(2))) : 0,
      returned_percent: count > 0 ? Number((((returnedCount / count) * 100).toFixed(2))) : 0,
    };
  });

  if (timelineView === 'daily') {
    return normalizedPoints;
  }

  const grouped = new Map();

  normalizedPoints.forEach((point) => {
    let key = point.date;
    if (timelineView === 'weekly') {
      const dateObj = new Date(`${point.date}T00:00:00`);
      const weekStart = new Date(dateObj);
      const day = weekStart.getDay();
      weekStart.setDate(weekStart.getDate() - day);
      key = `Wk ${formatDate(weekStart)}`;
    } else if (timelineView === 'monthly') {
      key = point.date.slice(0, 7);
    } else {
      key = point.date.slice(0, 4);
    }
    const current = grouped.get(key) || {
      count: 0,
      cancelled_count: 0,
      returned_count: 0,
    };

    grouped.set(key, {
      count: current.count + Number(point.count || 0),
      cancelled_count: current.cancelled_count + Number(point.cancelled_count || 0),
      returned_count: current.returned_count + Number(point.returned_count || 0),
    });
  });

  return Array.from(grouped.entries())
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([date, value]) => {
      const count = Number(value.count || 0);
      const cancelledCount = Number(value.cancelled_count || 0);
      const returnedCount = Number(value.returned_count || 0);
      return {
        date,
        count,
        cancelled_count: cancelledCount,
        returned_count: returnedCount,
        cancelled_percent: count > 0 ? Number((((cancelledCount / count) * 100).toFixed(2))) : 0,
        returned_percent: count > 0 ? Number((((returnedCount / count) * 100).toFixed(2))) : 0,
      };
    });
};

const DailyCountBarGraph = ({
  points,
  emptyStateMessage,
  timelineView,
  showPercentLines = false,
  valueSuffix = '',
  barTitleBuilder,
  targetThresholdPercent = null,
}) => {
  const maxCount = useMemo(() => {
    return Math.max(1, ...points.map((point) => point.count));
  }, [points]);

  const cancelledLine = useMemo(() => {
    if (!showPercentLines || points.length < 2) return '';
    return points
      .map((point, index) => {
        const x = (index / (points.length - 1)) * 100;
        const y = 100 - Number(point.cancelled_percent || 0);
        return `${x},${Math.max(0, Math.min(100, y))}`;
      })
      .join(' ');
  }, [points, showPercentLines]);

  const returnedLine = useMemo(() => {
    if (!showPercentLines || points.length < 2) return '';
    return points
      .map((point, index) => {
        const x = (index / (points.length - 1)) * 100;
        const y = 100 - Number(point.returned_percent || 0);
        return `${x},${Math.max(0, Math.min(100, y))}`;
      })
      .join(' ');
  }, [points, showPercentLines]);

  const thresholdY = useMemo(() => {
    if (typeof targetThresholdPercent !== 'number') return null;
    return Math.max(0, Math.min(100, 100 - targetThresholdPercent));
  }, [targetThresholdPercent]);

  if (!points.length) {
    return <Alert variant="info" className="mb-0">{emptyStateMessage}</Alert>;
  }

  const step = Math.max(1, Math.ceil(points.length / 12));
  const chartHeightPx = 220;

  return (
    <div className="registration-chart-wrapper">
      <div className="registration-chart registration-chart-relative d-flex align-items-end gap-2">
        {showPercentLines && points.length > 1 && (
          <svg className="registration-line-overlay" viewBox="0 0 100 100" preserveAspectRatio="none">
            <polyline className="line-cancelled" points={cancelledLine} />
            <polyline className="line-returned" points={returnedLine} />
          </svg>
        )}
        {thresholdY !== null && (
          <svg className="registration-threshold-overlay" viewBox="0 0 100 100" preserveAspectRatio="none">
            <line className="line-threshold" x1="0" y1={thresholdY} x2="100" y2={thresholdY} />
            <text className="line-threshold-label" x="99" y={Math.max(6, thresholdY - 1)} textAnchor="end">
              Target {targetThresholdPercent}%
            </text>
          </svg>
        )}
        {points.map((point, index) => {
          const heightPx = Math.max(6, Math.round((point.count / maxCount) * chartHeightPx));
          const showLabel = index % step === 0 || index === points.length - 1;
          return (
            <div key={point.date} className="registration-chart-bar-wrap flex-fill text-center">
              <div
                className="registration-chart-bar mx-auto"
                title={barTitleBuilder ? barTitleBuilder(point) : `${point.date}: ${point.count}${valueSuffix}`}
                style={{ height: `${heightPx}px` }}
              />
              <div className="registration-chart-label small text-muted mt-1">
                {showLabel ? point.date : ''}
              </div>
              {showPercentLines && (
                <div className="small text-muted">
                  C {Number(point.cancelled_percent || 0).toFixed(1)}% | R {Number(point.returned_percent || 0).toFixed(1)}%
                </div>
              )}
            </div>
          );
        })}
      </div>
      {showPercentLines && (
        <div className="d-flex gap-3 mt-2 small">
          <span className="legend-cancelled">Cancelled %</span>
          <span className="legend-returned">Returned %</span>
        </div>
      )}
      {thresholdY !== null && (
        <div className="d-flex gap-3 mt-1 small">
          <span className="legend-threshold">Target {targetThresholdPercent}%</span>
        </div>
      )}
      <div className="d-flex justify-content-between mt-2 small text-muted">
        <span>{points[0]?.date}</span>
        <span>{points[points.length - 1]?.date}</span>
      </div>
      <div className="mt-1 small text-muted">
        {points.length > 12
          ? `Showing ${points.length} ${timelineView} bucket(s). Labels repeat every ${step} bucket(s).`
          : `Showing ${points.length} ${timelineView} bucket(s).`}
      </div>
    </div>
  );
};

const RoleAnalyticsDashboard = ({ roleLabel = 'Dashboard' }) => {
  const [activeDashboard, setActiveDashboard] = useState(DASHBOARD_ITEMS[0].id);
  const [selectedPreset, setSelectedPreset] = useState('today');
  const [timelineView, setTimelineView] = useState('daily');
  const initialRange = getPresetRange('today');
  const [startDate, setStartDate] = useState(initialRange.startDate);
  const [endDate, setEndDate] = useState(initialRange.endDate);

  const [points, setPoints] = useState([]);
  const [topSellingRows, setTopSellingRows] = useState([]);
  const [criticalInventoryRows, setCriticalInventoryRows] = useState([]);
  const [criticalThreshold, setCriticalThreshold] = useState(10);
  const [topSellingViewMode, setTopSellingViewMode] = useState('products');
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const currentDashboard = useMemo(
    () => DASHBOARD_ITEMS.find((item) => item.id === activeDashboard) || DASHBOARD_ITEMS[0],
    [activeDashboard]
  );

  const chartPoints = useMemo(
    () => aggregateTimelinePoints(points, timelineView),
    [points, timelineView]
  );

  const loadChartData = async (dashboardId, start, end) => {
    try {
      setLoading(true);
      setError('');
      const dashboard = DASHBOARD_ITEMS.find((item) => item.id === dashboardId) || DASHBOARD_ITEMS[0];
      if (dashboard.type === 'top-selling') {
        const response = await axios.get(dashboard.endpoint, {
          params: {
            start_date: start,
            end_date: end,
            timeline_view: timelineView,
            view_mode: topSellingViewMode,
            limit: 20,
          },
        });

        const rows = Array.isArray(response.data?.rows) ? response.data.rows : [];
        setTopSellingRows(rows);
        setPoints([]);
        setCriticalInventoryRows([]);
        setTotalCount(Number(response.data?.[dashboard.totalKey] || 0));
      } else if (dashboard.type === 'critical-inventory') {
        const response = await axios.get(dashboard.endpoint, {
          params: {
            start_date: start,
            end_date: end,
            timeline_view: timelineView,
          },
        });

        const rows = Array.isArray(response.data?.rows) ? response.data.rows : [];
        setCriticalInventoryRows(rows);
        setCriticalThreshold(Number(response.data?.threshold_percent || 10));
        setPoints([]);
        setTopSellingRows([]);
        setTotalCount(Number(response.data?.[dashboard.totalKey] || 0));
      } else if (dashboard.type === 'login-rate') {
        const response = await axios.get(dashboard.endpoint, {
          params: {
            start_date: start,
            end_date: end,
            timeline_view: timelineView,
          },
        });

        const responsePoints = Array.isArray(response.data?.points)
          ? response.data.points.map((point) => ({
            date: point.bucket,
            count: Number(point.login_percent || 0),
            logged_in_users: Number(point.logged_in_users || 0),
          }))
          : [];
        setPoints(responsePoints);
        setCriticalInventoryRows([]);
        setTopSellingRows([]);
        setTotalCount(Number(response.data?.[dashboard.totalKey] || 0));
      } else {
        const response = await axios.get(dashboard.endpoint, {
          params: {
            start_date: start,
            end_date: end,
          },
        });

        const responsePoints = Array.isArray(response.data?.points) ? response.data.points : [];
        setPoints(responsePoints);
        setCriticalInventoryRows([]);
        setTopSellingRows([]);
        setTotalCount(Number(response.data?.[dashboard.totalKey] || 0));
      }
    } catch (loadError) {
      setError(loadError.response?.data?.detail || 'Failed to load dashboard statistics');
      setPoints([]);
      setTopSellingRows([]);
      setCriticalInventoryRows([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadChartData(activeDashboard, startDate, endDate);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeDashboard]);

  useEffect(() => {
    if (activeDashboard === 'top-selling' || activeDashboard === 'user-login-rate' || activeDashboard === 'critical-inventory') {
      loadChartData(activeDashboard, startDate, endDate);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [topSellingViewMode, timelineView]);

  const applyPreset = async (preset) => {
    const range = getPresetRange(preset);
    setSelectedPreset(preset);
    setStartDate(range.startDate);
    setEndDate(range.endDate);
    await loadChartData(activeDashboard, range.startDate, range.endDate);
  };

  const applyCustomRange = async (event) => {
    event.preventDefault();
    setSelectedPreset('custom');
    await loadChartData(activeDashboard, startDate, endDate);
  };

  const handleStartDateChange = (value) => {
    setSelectedPreset('custom');
    setStartDate(value);
  };

  const handleEndDateChange = (value) => {
    setSelectedPreset('custom');
    setEndDate(value);
  };

  return (
    <Container fluid className="py-4">
      <Row className="g-3">
        <Col lg={3} xl={2}>
          <Card className="shadow-sm border-0 h-100">
            <Card.Body>
              <div className="d-flex align-items-center justify-content-between mb-3">
                <h2 className="h6 mb-0">Dashboards</h2>
                <Badge bg="dark">{DASHBOARD_ITEMS.length}</Badge>
              </div>
              <ListGroup>
                {DASHBOARD_ITEMS.map((item) => (
                  <ListGroup.Item
                    action
                    key={item.id}
                    active={activeDashboard === item.id}
                    onClick={() => setActiveDashboard(item.id)}
                  >
                    <div className="fw-semibold">{item.title}</div>
                    <div className="small opacity-75">{item.description}</div>
                  </ListGroup.Item>
                ))}
              </ListGroup>
            </Card.Body>
          </Card>
        </Col>

        <Col lg={9} xl={10}>
          <Card className="shadow-sm border-0 mb-3">
            <Card.Body>
              <h1 className="h4 mb-1">{roleLabel} Analytics</h1>
              <p className="text-muted mb-0">
                This area will keep expanding with more dashboards over time.
              </p>
            </Card.Body>
          </Card>

          {currentDashboard && (
            activeDashboard === 'product-views' ? (
              <ProductViewsDashboard />
            ) : (
            <Card className="shadow-sm border-0">
              <Card.Body>
                <div className="d-flex flex-wrap align-items-start justify-content-between gap-3 mb-3">
                  <div>
                    <h3 className="h5 mb-1">{currentDashboard.graphTitle}</h3>
                    <p className="text-muted mb-0">{currentDashboard.graphDescription}</p>
                  </div>
                  <Badge bg="primary" pill className="fs-6">{currentDashboard.totalLabel}: {totalCount}</Badge>
                </div>

                {error && <Alert variant="danger">{error}</Alert>}

                <Form onSubmit={applyCustomRange} className="mb-4">
                  <Row className="g-2 align-items-start">
                    <Col md={12} lg={7}>
                      <div className="d-flex flex-wrap gap-3">
                        <Form.Check
                          type="checkbox"
                          id="preset-today"
                          label="Today"
                          checked={selectedPreset === 'today'}
                          onChange={() => applyPreset('today')}
                        />
                        <Form.Check
                          type="checkbox"
                          id="preset-week"
                          label="This Week"
                          checked={selectedPreset === 'week'}
                          onChange={() => applyPreset('week')}
                        />
                        <Form.Check
                          type="checkbox"
                          id="preset-month"
                          label="This Month"
                          checked={selectedPreset === 'month'}
                          onChange={() => applyPreset('month')}
                        />
                        <Form.Check
                          type="checkbox"
                          id="preset-year"
                          label="This Year"
                          checked={selectedPreset === 'year'}
                          onChange={() => applyPreset('year')}
                        />
                      </div>
                    </Col>
                    <Col md={6} lg={2}>
                      <Form.Group>
                        <Form.Label>Start Date</Form.Label>
                        <Form.Control
                          type="date"
                          value={startDate}
                          max={endDate}
                          onChange={(event) => handleStartDateChange(event.target.value)}
                        />
                      </Form.Group>
                    </Col>
                    <Col md={6} lg={2}>
                      <Form.Group>
                        <Form.Label>End Date</Form.Label>
                        <Form.Control
                          type="date"
                          value={endDate}
                          min={startDate}
                          onChange={(event) => handleEndDateChange(event.target.value)}
                        />
                      </Form.Group>
                    </Col>
                    <Col md={6} lg={2}>
                      <Form.Group>
                        <Form.Label>Timeline View</Form.Label>
                        <Form.Select
                          value={timelineView}
                          onChange={(event) => setTimelineView(event.target.value)}
                        >
                          <option value="daily">Daily</option>
                          <option value="weekly">Weekly</option>
                          <option value="monthly">Monthly</option>
                          <option value="yearly">Yearly</option>
                        </Form.Select>
                      </Form.Group>
                    </Col>
                    {currentDashboard.type === 'top-selling' && (
                      <Col md={6} lg={2}>
                        <Form.Group>
                          <Form.Label>View Mode</Form.Label>
                          <Form.Select
                            value={topSellingViewMode}
                            onChange={(event) => setTopSellingViewMode(event.target.value)}
                          >
                            <option value="products">Products</option>
                            <option value="variants">Variants</option>
                          </Form.Select>
                        </Form.Group>
                      </Col>
                    )}
                    <Col lg={1} className="pt-4">
                      <button type="submit" className="btn btn-primary btn-uniform-height w-100">Apply</button>
                    </Col>
                  </Row>
                </Form>

                {loading ? (
                  <div className="text-center py-5">
                    <Spinner animation="border" />
                  </div>
                ) : currentDashboard.type === 'top-selling' ? (
                  topSellingRows.length === 0 ? (
                    <Alert variant="info" className="mb-0">{currentDashboard.emptyStateMessage}</Alert>
                  ) : (
                    <Table responsive bordered hover>
                      <thead>
                        <tr>
                          <th>#</th>
                          <th>Timeline Bucket</th>
                          <th>{topSellingViewMode === 'products' ? 'Product' : 'Variant'}</th>
                          {topSellingViewMode === 'variants' && <th>Variant SKU</th>}
                          <th>Unit Price</th>
                          <th>Units Sold</th>
                        </tr>
                      </thead>
                      <tbody>
                        {topSellingRows.map((row, index) => (
                          <tr key={`${row.bucket}-${row.product_id}-${row.variant_id || 'product'}-${index}`}>
                            <td>{index + 1}</td>
                            <td>{row.bucket}</td>
                            <td>{row.display_name || row.product_name}</td>
                            {topSellingViewMode === 'variants' && <td>{row.variant_sku || '-'}</td>}
                            <td>{Number(row.unit_price || 0).toFixed(2)}</td>
                            <td>{row.units_sold}</td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  )
                ) : currentDashboard.type === 'critical-inventory' ? (
                  criticalInventoryRows.length === 0 ? (
                    <Alert variant="info" className="mb-0">{currentDashboard.emptyStateMessage}</Alert>
                  ) : (
                    <>
                      <div className="small text-muted mb-2">Configured threshold: {criticalThreshold}%</div>
                      <Table responsive bordered hover>
                        <thead>
                          <tr>
                            <th>#</th>
                            <th>Timeline Bucket</th>
                            <th>Product</th>
                            <th>Variant SKU</th>
                            <th>Available Qty</th>
                            <th>Ordered Qty</th>
                            <th>Critical %</th>
                            <th>Threshold %</th>
                            <th>Severity</th>
                          </tr>
                        </thead>
                        <tbody>
                          {criticalInventoryRows.map((row, index) => {
                            const severity = getCriticalSeverityBand(row.critical_percent);
                            return (
                              <tr key={`${row.bucket}-${row.variant_id}-${index}`} className={severity.rowClass}>
                                <td>{index + 1}</td>
                                <td>{row.bucket}</td>
                                <td>{row.product_name}</td>
                                <td>{row.variant_sku}</td>
                                <td>{row.available_qty}</td>
                                <td>{row.ordered_qty}</td>
                                <td>{Number(row.critical_percent || 0).toFixed(2)}%</td>
                                <td>{Number(row.threshold_percent || 0).toFixed(2)}%</td>
                                <td>
                                  <span className={`severity-badge ${severity.badgeClass}`}>{severity.label}</span>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </Table>
                    </>
                  )
                ) : (
                  <DailyCountBarGraph
                    points={currentDashboard.type === 'login-rate' ? points : chartPoints}
                    timelineView={timelineView}
                    emptyStateMessage={currentDashboard.emptyStateMessage}
                    showPercentLines={Boolean(currentDashboard.showPercentLines)}
                    valueSuffix={currentDashboard.type === 'login-rate' ? '%' : ''}
                    barTitleBuilder={currentDashboard.type === 'login-rate'
                      ? (point) => `${point.date}: ${Number(point.count || 0).toFixed(2)}% (${point.logged_in_users || 0} users)`
                      : undefined}
                    targetThresholdPercent={currentDashboard.type === 'login-rate'
                      ? currentDashboard.targetThresholdPercent
                      : null}
                  />
                )}
              </Card.Body>
            </Card>
            )
          )}
        </Col>
      </Row>
    </Container>
  );
};

export default RoleAnalyticsDashboard;
